import { ChatContextScope, ContextFile } from './types';

/**
 * The "@" context picker's selection (chat-ui-spec §4.5–4.9): whole document types and single
 * files. A selected type covers its files; ticking one file inside a selected type narrows the
 * selection to just that file.
 */

export interface ContextSelection {
  types: string[];
  /** `[file_id, file_name]`, in the order picked. */
  files: Array<[string, string]>;
}

export const EMPTY_SELECTION: ContextSelection = { types: [], files: [] };

/** The server returns at most this many files per request, and the UI always asks for it. */
export const FILE_LIMIT = 200;

export const SEARCH_DEBOUNCE_MS = 180;

export const isEmptySelection = (sel: ContextSelection): boolean =>
  sel.types.length === 0 && sel.files.length === 0;

export const selectionCount = (sel: ContextSelection): number =>
  sel.types.length + sel.files.length;

export const isTypeSelected = (sel: ContextSelection, docType: string): boolean =>
  sel.types.includes(docType);

/** A file shows as ticked when it is picked itself or its whole type is. */
export const isFileSelected = (
  sel: ContextSelection,
  file: Pick<ContextFile, 'file_id' | 'doc_type'>,
): boolean => sel.files.some(([id]) => id === file.file_id) || sel.types.includes(file.doc_type);

/**
 * Ticks or unticks a whole type. Ticking it drops the files of that type already picked, as far
 * as the loaded file lists know them, because the type now covers them.
 */
export function toggleType(
  sel: ContextSelection,
  docType: string,
  knownFiles: Record<string, ContextFile[]>,
): ContextSelection {
  if (sel.types.includes(docType)) {
    return { ...sel, types: sel.types.filter((t) => t !== docType) };
  }
  const ofType = new Set<string>();
  Object.values(knownFiles).forEach((files) =>
    files.forEach((f) => {
      if (f.doc_type === docType) ofType.add(f.file_id);
    }),
  );
  return {
    types: [...sel.types, docType],
    files: sel.files.filter(([id]) => !ofType.has(id)),
  };
}

/** Ticks or unticks one file; inside a ticked type it narrows the type to just this file. */
export function toggleFile(sel: ContextSelection, file: ContextFile): ContextSelection {
  if (sel.files.some(([id]) => id === file.file_id)) {
    return { ...sel, files: sel.files.filter(([id]) => id !== file.file_id) };
  }
  return {
    types: sel.types.filter((t) => t !== file.doc_type),
    files: [...sel.files, [file.file_id, file.file_name]],
  };
}

export const removeType = (sel: ContextSelection, docType: string): ContextSelection => ({
  ...sel,
  types: sel.types.filter((t) => t !== docType),
});

export const removeFile = (sel: ContextSelection, fileId: string): ContextSelection => ({
  ...sel,
  files: sel.files.filter(([id]) => id !== fileId),
});

/** What the chat request carries, or undefined (the key omitted) when nothing is selected. */
export function contextPayload(sel: ContextSelection): ChatContextScope | undefined {
  if (isEmptySelection(sel)) return undefined;
  return { doc_types: [...sel.types], file_ids: sel.files.map(([id]) => id) };
}

/** The labels shown under a sent question: `@type` for each type, then each file name. */
export const contextTagLabels = (sel: ContextSelection): { types: string[]; files: string[] } => ({
  types: [...sel.types],
  files: sel.files.map(([, name]) => name),
});

/** Cache key for one files request: the type filter and the search term. */
export const filesCacheKey = (docType: string, term: string): string => `${docType}\u0000${term}`;

export const pickerFooter = (sel: ContextSelection): string =>
  isEmptySelection(sel)
    ? 'Tick document types, files, or both.'
    : 'The answer will use only these documents.';

export const filesHeadingNote = (docType: string): string =>
  docType ? `in ${docType}` : 'across every type';

export const emptyFilesText = (term: string): string =>
  term ? 'No indexed file matches that.' : 'No files here.';

export const NO_DOC_TYPES_TEXT = 'No indexed documents to focus on in this project.';

export const TOO_MANY_FILES_TEXT = `Only the first ${FILE_LIMIT} files are listed — search, or pick a type in the dropdown, to see others.`;

export type ExtensionFamily = 'pdf' | 'doc' | 'sheet' | 'cad' | 'image' | 'email' | 'other';

const FAMILIES: Array<[ExtensionFamily, string[]]> = [
  ['pdf', ['pdf']],
  ['doc', ['doc', 'docx', 'rtf', 'txt', 'md']],
  ['sheet', ['xls', 'xlsx', 'xlsm', 'csv']],
  ['cad', ['dwg', 'dxf', 'dgn', 'rvt', 'ifc']],
  ['image', ['png', 'jpg', 'jpeg', 'tif', 'tiff', 'gif', 'heic', 'bmp']],
  ['email', ['eml', 'msg']],
];

const normalExt = (ext: string | null | undefined): string =>
  (ext ?? '').trim().replace(/^\.+/, '').toLowerCase();

export function extensionFamily(ext: string | null | undefined): ExtensionFamily {
  const e = normalExt(ext);
  const match = FAMILIES.find(([, list]) => list.includes(e));
  return match ? match[0] : 'other';
}

/** The badge text: up to four capitals, or "–" when no extension was recorded. */
export function extensionBadge(ext: string | null | undefined): string {
  const e = normalExt(ext);
  return e ? e.slice(0, 4).toUpperCase() : '–';
}

export const extensionTitle = (ext: string | null | undefined): string => {
  const e = normalExt(ext);
  return e ? `.${e}` : 'no extension recorded';
};

/** Badge backgrounds per family (white text), from the CENTERLINE AI design. */
export const EXTENSION_COLORS: Record<ExtensionFamily, string> = {
  pdf: '#D02C14',
  doc: '#0947B9',
  sheet: '#2E7D32',
  cad: '#8D5524',
  image: '#7B3FA0',
  email: '#0F766E',
  other: '#949494',
};
